import { useEffect, useMemo, useRef, useState } from 'react';
import type { AttributeDef, TaskUI } from '@flexiplanner/types';
import { Button } from '@/components/ui/button';
import { EnumAttributeInput } from '@/components/attributes/EnumAttributeInput';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import {
  CheckCircle2,
  Gauge,
  Maximize2,
  Minimize2,
  Pause,
  Play,
  Sparkles,
  Target,
  Waves,
  X,
} from 'lucide-react';
import { cn, formatDuration } from '@/lib/utils';
import { useTasksStore } from '@/stores/tasksStore';

interface ImmersionModeProps {
  task: TaskUI;
  onClose: () => void;
  onMinimize: () => void;
  onComplete: () => void;
}

interface FlowMarker {
  timestamp: string;
  elapsedSeconds: number;
  depth: number | null;
  note: string;
}

interface FlowDepthChange {
  timestamp: string;
  elapsedSeconds: number;
  previousDepth: number;
  depth: number;
}

function formatStopwatch(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

function ProgressRing({
  progress,
  children,
}: {
  progress: number;
  children: React.ReactNode;
}) {
  const size = 236;
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - Math.max(0, Math.min(1, progress)));

  return (
    <div className="relative mx-auto" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-white/10"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="text-cyan-300 transition-[stroke-dashoffset] duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        {children}
      </div>
    </div>
  );
}

const scoreFields = [
  ['productivityInvestment', 'P investment'],
  ['rejuvenationInvestment', 'RJ investment'],
  ['productivityReturn', 'P return'],
  ['rejuvenationReturn', 'RJ return'],
  ['resistance', 'Resistance'],
  ['focusLevel', 'Focus'],
] as const;

export function ImmersionMode({ task, onClose, onMinimize, onComplete }: ImmersionModeProps) {
  const { toast } = useToast();
  const updateTask = useTasksStore(s => s.updateTask);
  const [isRunning, setIsRunning] = useState(false);
  const [sessionStartedAtMs, setSessionStartedAtMs] = useState<number | null>(null);
  const [pauseSecondsOffset, setPauseSecondsOffset] = useState(0);
  const [clockTick, setClockTick] = useState(() => Date.now());
  const [pauseCount, setPauseCount] = useState(0);
  const [pauseLog, setPauseLog] = useState<{ timestamp: string; duration: number; reason: string }[]>([]);
  const [currentPauseStart, setCurrentPauseStart] = useState<number | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [activeSegmentStart, setActiveSegmentStart] = useState<number | null>(null);
  const [segments, setSegments] = useState<Array<{ type: 'work' | 'pause'; start: string; end: string; reason?: string }>>([]);
  const [phase, setPhase] = useState<'running' | 'finish'>('running');
  const [isExitPromptOpen, setIsExitPromptOpen] = useState(false);
  const [isSplitFullscreen, setIsSplitFullscreen] = useState(false);
  const [flowMarkers, setFlowMarkers] = useState<FlowMarker[]>([]);
  const [flowDepth, setFlowDepth] = useState(7);
  const [flowDepthChanged, setFlowDepthChanged] = useState(false);
  const [flowDepthChanges, setFlowDepthChanges] = useState<FlowDepthChange[]>([]);
  const flowDepthChangesRef = useRef<FlowDepthChange[]>([]);
  const [completionPercent, setCompletionPercent] = useState(task.completionPercent ?? 0);
  const [notes, setNotes] = useState('');
  const [emotionWord, setEmotionWord] = useState(String(task.customAttributes?.emotion_word || ''));
  const [attributes, setAttributes] = useState<AttributeDef[]>([]);
  const [actualState, setActualState] = useState({
    productivityInvestment: task.stateProfile?.required_productivity_energy ?? 5,
    rejuvenationInvestment: task.stateProfile?.required_rejuvenation_energy ?? 5,
    productivityReturn: task.stateProfile?.productivity_return ?? 5,
    rejuvenationReturn: task.stateProfile?.rejuvenation_return ?? 5,
    resistance: task.stateProfile?.resistance ?? 5,
    focusLevel: 7,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const flowInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const checkActiveSession = async () => {
      try {
        const response = await api.training.getActiveImmersions();
        const activeSession = Array.isArray(response.data)
          ? response.data.find((session: any) => session.task_id === task.id)
          : null;
        if (response.success && activeSession) {
          setSessionId(activeSession.id);
          const startTime = new Date(activeSession.start_time).getTime();
          const totalPauseSeconds = Array.isArray(activeSession.pauseEvents)
            ? activeSession.pauseEvents.reduce((acc: number, p: any) => acc + (p.duration || 0), 0)
            : 0;
          setSessionStartedAtMs(startTime);
          setPauseSecondsOffset(totalPauseSeconds);
          setClockTick(Date.now());
          setIsRunning(true);
          setActiveSegmentStart(Date.now());
        }
      } catch (error) {
        console.error('Failed to fetch active session:', error);
      } finally {
        setIsLoadingSession(false);
      }
    };
    void checkActiveSession();
  }, [task.id]);

  useEffect(() => {
    let cancelled = false;
    async function loadAttributes() {
      try {
        const response = await api.user.getAttributes();
        if (!cancelled && response.success && Array.isArray(response.data)) {
          setAttributes(response.data);
          const emotionAttr = response.data.find(isEmotionAttribute);
          setEmotionWord(String(getEmotionValue(task.customAttributes || {}, emotionAttr) || ''));
        }
      } catch (error) {
        console.error('Failed to load immersion attributes:', error);
      }
    }
    void loadAttributes();
    return () => {
      cancelled = true;
    };
  }, [task.customAttributes]);

  const emotionAttribute = useMemo(() => attributes.find(isEmotionAttribute), [attributes]);

  const elapsedTime = useMemo(() => {
    if (!sessionStartedAtMs) return 0;
    const effectiveNow = currentPauseStart ?? clockTick;
    return Math.max(0, Math.floor((effectiveNow - sessionStartedAtMs) / 1000) - pauseSecondsOffset);
  }, [clockTick, currentPauseStart, pauseSecondsOffset, sessionStartedAtMs]);

  useEffect(() => {
    if (!sessionStartedAtMs || (!isRunning && !currentPauseStart)) return;
    const updateClock = () => setClockTick(Date.now());
    updateClock();
    const interval = window.setInterval(updateClock, 1000);
    window.addEventListener('focus', updateClock);
    document.addEventListener('visibilitychange', updateClock);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', updateClock);
      document.removeEventListener('visibilitychange', updateClock);
    };
  }, [currentPauseStart, isRunning, sessionStartedAtMs]);

  const progress = useMemo(
    () => (task.estimatedDuration ? Math.min(elapsedTime / (task.estimatedDuration * 60), 1) : 0),
    [elapsedTime, task.estimatedDuration]
  );

  async function handleStart() {
    try {
      if (!sessionId) {
        setIsStarting(true);
        const response = await api.training.startImmersion(task.id, task.estimatedDuration || 30, true);
        if (response.data?.sessionId) setSessionId(response.data.sessionId);
        const startedAt = response.data?.startTime ? new Date(response.data.startTime).getTime() : Date.now();
        setSessionStartedAtMs(startedAt);
        setPauseSecondsOffset(0);
        setClockTick(Date.now());
        const slot = response.data?.schedule;
        if (slot?.start && slot?.end) {
          updateTask(task.id, { status: 'in_progress', scheduledStart: slot.start, scheduledEnd: slot.end });
        }
      }

      const resumeAt = Date.now();
      if (currentPauseStart && sessionId) {
        const pauseDuration = Math.floor((resumeAt - currentPauseStart) / 1000);
        api.training.pauseImmersion(sessionId, pauseDuration, 'break').catch(console.error);
        setPauseLog(prev => [...prev, { timestamp: new Date(currentPauseStart).toISOString(), duration: pauseDuration, reason: 'break' }]);
        setSegments(prev => [...prev, { type: 'pause', start: new Date(currentPauseStart).toISOString(), end: new Date(resumeAt).toISOString(), reason: 'break' }]);
        setPauseSecondsOffset(prev => prev + pauseDuration);
        setCurrentPauseStart(null);
      }

      if (!sessionStartedAtMs) setSessionStartedAtMs(resumeAt);
      setClockTick(resumeAt);
      setIsRunning(true);
      setActiveSegmentStart(resumeAt);
    } catch (error) {
      console.error('Failed to start immersion session:', error);
      toast({ title: 'Could not start activity', description: 'Try again from the Activity card.', variant: 'destructive' });
    } finally {
      setIsStarting(false);
    }
  }

  useEffect(() => {
    if (!isLoadingSession && !sessionId && !isStarting) void handleStart();
  }, [isLoadingSession, sessionId, isStarting]);

  function handlePause(reason = 'break') {
    const pauseStart = Date.now();
    if (activeSegmentStart) {
      setSegments(prev => [...prev, { type: 'work', start: new Date(activeSegmentStart).toISOString(), end: new Date(pauseStart).toISOString() }]);
    }
    setIsRunning(false);
    setPauseCount(prev => prev + 1);
    setCurrentPauseStart(pauseStart);
    setClockTick(pauseStart);
    setActiveSegmentStart(null);
    if (sessionId) api.training.pauseImmersion(sessionId, 0, reason).catch(console.error);
  }

  function markFlowState() {
    const note = flowInputRef.current?.value.trim() || '';
    setFlowMarkers(prev => [
      ...prev,
      {
        timestamp: new Date().toISOString(),
        elapsedSeconds: elapsedTime,
        depth: flowDepthChanged ? flowDepth : null,
        note,
      },
    ]);
    if (flowInputRef.current) flowInputRef.current.value = '';
    toast({ title: 'Flow state marked', description: `Captured at ${formatStopwatch(elapsedTime)}.` });
  }

  function handleFlowDepthChange(nextDepth: number) {
    const depth = Math.max(0, Math.min(10, nextDepth));
    if (depth === flowDepth) return;
    const timestamp = new Date().toISOString();
    const change = {
      timestamp,
      elapsedSeconds: elapsedTime,
      previousDepth: flowDepth,
      depth,
    };
    flowDepthChangesRef.current = [...flowDepthChangesRef.current, change];
    setFlowDepthChanges(flowDepthChangesRef.current);
    setFlowDepth(depth);
    setFlowDepthChanged(true);
  }

  function beginFinish() {
    setIsExitPromptOpen(false);
    const stopTime = Date.now();
    setClockTick(stopTime);
    if (activeSegmentStart) {
      setSegments(prev => [...prev, { type: 'work', start: new Date(activeSegmentStart).toISOString(), end: new Date(stopTime).toISOString() }]);
      setActiveSegmentStart(null);
    }
    setIsRunning(false);
    setPhase('finish');
  }

  function handleExitClick() {
    setIsExitPromptOpen(true);
  }

  function handleMinimize() {
    setIsExitPromptOpen(false);
    setIsSplitFullscreen(false);
    onMinimize();
  }

  function handleAttributeUpdated(updated: AttributeDef) {
    setAttributes(current => current.map(attr => (attr.attr_id === updated.attr_id ? updated : attr)));
  }

  async function submitFinish(completed: boolean) {
    setIsSubmitting(true);
    const now = new Date().toISOString();
    const percent = completed ? 100 : Math.max(0, Math.min(99, completionPercent));
    const completionBefore = task.completionPercent ?? 0;
    const finalSegments = [...segments];

    try {
      if (sessionId) {
        await api.training.endImmersion(sessionId, completed, actualState.productivityInvestment, {
          completionPercent: percent,
          completionBefore,
          focusLevel: actualState.focusLevel,
          actualState,
          emotionWord,
          flowMarkers,
          flowDepthChanges: flowDepthChanged ? flowDepthChangesRef.current : [],
          notes,
          segments: finalSegments,
        });
      }

      await api.training.submitFeedback({
        taskId: task.id,
        scheduledStart: task.scheduledStart,
        scheduledEnd: task.scheduledEnd,
        actualStart: finalSegments[0]?.start,
        actualEnd: now,
        completed,
        actualDuration: elapsedTime / 60,
        energyLevel: actualState.productivityInvestment,
        focusLevel: Math.max(1, Math.min(5, Math.round(actualState.focusLevel / 2))),
        pauseCount,
        pauseLog: pauseLog.map(p => ({ timestamp: p.timestamp, durationSeconds: p.duration })),
        notes: JSON.stringify({
          actualState,
          emotionWord,
          flowMarkers,
          flowDepthChanges: flowDepthChanged ? flowDepthChangesRef.current : [],
          notes,
        }),
        userFeedback: { wasAccurate: true, notes },
      });

      const remainingDuration = completed
        ? 0
        : Math.max(15, Math.round((task.estimatedDuration || 30) * (100 - percent) / 100));
      updateTask(task.id, {
        status: completed ? 'completed' : percent > 0 ? 'in_progress' : 'todo',
        completionPercent: percent,
        remainingDuration,
        scheduledStart: finalSegments[0]?.start || task.scheduledStart,
        scheduledEnd: now,
        stateProfile: {
          ...(task.stateProfile || {}),
          required_productivity_energy: actualState.productivityInvestment,
          required_rejuvenation_energy: actualState.rejuvenationInvestment,
          productivity_return: actualState.productivityReturn,
          rejuvenation_return: actualState.rejuvenationReturn,
          resistance: actualState.resistance,
        },
        customAttributes: {
          ...(task.customAttributes || {}),
          emotion_word: emotionWord || null,
          latest_session: {
            completion_before: completionBefore,
            completion_after: percent,
            completion_delta: Math.max(0, percent - completionBefore),
            actual_duration_minutes: Math.max(1, Math.round(elapsedTime / 60)),
            emotion_word: emotionWord || null,
            ended_at: now,
          },
        },
      });

      toast({
        title: completed ? 'Activity completed' : 'Activity updated',
        description: 'Actual duration, flow, P/RJ, and resistance were captured.',
      });
      onComplete();
    } catch (error) {
      console.error('Failed to finish immersion session:', error);
      toast({ title: 'Could not save activity data', description: 'Your timer is still visible. Try again.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  }

  async function cancelSession() {
    setIsSubmitting(true);
    try {
      if (sessionId) {
        const response = await api.training.cancelImmersion(sessionId);
        if (!response.success) throw new Error(response.error?.message || 'Could not cancel session');
      }
      updateTask(task.id, {
        status: (task.completionPercent ?? 0) > 0 ? 'in_progress' : 'todo',
      });
      toast({
        title: 'Session cancelled',
        description: 'No completion data was saved for this activity session.',
      });
      onClose();
    } catch (error) {
      console.error('Failed to cancel immersion session:', error);
      toast({
        title: 'Could not cancel session',
        description: error instanceof Error ? error.message : 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoadingSession) {
    return (
      <Card className="border-cyan-300/30 bg-slate-950 text-white shadow-2xl">
        <CardContent className="flex items-center gap-3 p-4">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-cyan-300 border-b-transparent" />
          <p className="text-sm text-white/70">Starting activity...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <Card
      className={cn(
        'flex min-h-0 flex-1 flex-col overflow-hidden border-white/10 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.18),transparent_38%),linear-gradient(145deg,#07111f,#111827_58%,#151515)] text-white shadow-2xl md:flex-none',
        isSplitFullscreen && 'fixed inset-0 z-[80] h-dvh w-dvw rounded-none border-0'
      )}
    >
      <CardContent className={cn('native-scroll min-h-0 flex-1 space-y-4 overflow-y-auto p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]', isSplitFullscreen && 'flex min-h-dvh flex-col p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-[calc(1rem+env(safe-area-inset-top))] sm:p-6 lg:p-8')}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <Badge className={cn('border-white/10', isRunning ? 'bg-cyan-300 text-slate-950' : 'bg-white/10 text-white')}>
                {isRunning ? 'In flow' : phase === 'finish' ? 'Wrap-up' : 'Paused'}
              </Badge>
              {task.estimatedDuration && (
                <Badge variant="outline" className="border-white/15 text-white/75">
                  <Target className="mr-1 h-3 w-3" />
                  {formatDuration(task.estimatedDuration)}
                </Badge>
              )}
            </div>
            <h2 className="truncate text-lg font-semibold tracking-normal">{task.title}</h2>
            <p className="text-xs text-white/55">Concurrent tracking is on. Capture reality, not the plan.</p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10"
              onClick={() => setIsSplitFullscreen(value => !value)}
              aria-label={isSplitFullscreen ? 'Exit split-screen full screen' : 'Open split-screen full screen'}
              title={isSplitFullscreen ? 'Exit full screen' : 'Split-screen full screen'}
            >
              {isSplitFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10"
              onClick={handleMinimize}
              aria-label="Minimize immersive session"
              title="Minimize"
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10"
              onClick={handleExitClick}
              aria-label="Finish or cancel immersive session"
              title="Finish or cancel"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {phase === 'running' ? (
          <div
            className={cn(
              'space-y-4',
              isSplitFullscreen &&
                'grid flex-1 items-center gap-6 space-y-0 lg:grid-cols-[minmax(0,1fr)_minmax(360px,480px)]'
            )}
          >
            <div className={cn('space-y-4', isSplitFullscreen && 'flex min-h-[520px] flex-col justify-center')}>
              <ProgressRing progress={progress}>
                <span
                  className={cn(
                    'font-mono text-5xl font-semibold tabular-nums',
                    isSplitFullscreen && 'text-6xl sm:text-7xl'
                  )}
                >
                  {formatStopwatch(elapsedTime)}
                </span>
                <span className="mt-2 text-xs text-white/55">
                  {Math.round(progress * 100)}% of estimate
                </span>
              </ProgressRing>

              <div className="grid grid-cols-3 gap-2">
                {isRunning ? (
                  <Button type="button" variant="secondary" onClick={() => handlePause('break')}>
                    <Pause className="mr-2 h-4 w-4" />
                    Pause
                  </Button>
                ) : (
                  <Button type="button" onClick={handleStart} disabled={isStarting}>
                    <Play className="mr-2 h-4 w-4" />
                    Resume
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-11 border-white/15 bg-white/5 text-white hover:bg-white/10"
                  onClick={beginFinish}
                >
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Finish
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="min-h-11 text-white hover:bg-white/10"
                  onClick={cancelSession}
                  disabled={isSubmitting}
                >
                  <X className="mr-2 h-4 w-4" />
                  Cancel
                </Button>
              </div>
            </div>

            <div className={cn('space-y-4', isSplitFullscreen && 'rounded-lg border border-white/10 bg-white/[0.04] p-4')}>
              <div className="grid grid-cols-3 gap-2 text-center">
                <Metric icon={<Pause className="h-3.5 w-3.5" />} label="Pauses" value={String(pauseCount)} />
                <Metric icon={<Sparkles className="h-3.5 w-3.5" />} label="Flow marks" value={String(flowMarkers.length)} />
                <Metric icon={<Waves className="h-3.5 w-3.5" />} label="Depth changes" value={String(flowDepthChanges.length)} />
              </div>

              <div className="rounded-md border border-white/10 bg-white/[0.05] p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <Label className="text-xs text-white/70">Flow depth</Label>
                  <span className="font-mono text-xs text-cyan-200">{flowDepth}/10</span>
                </div>
                <Input
                  type="range"
                  min={0}
                  max={10}
                  value={flowDepth}
                  onChange={event => handleFlowDepthChange(Number(event.target.value))}
                  className="h-8"
                />
                <p className="mt-1 text-[11px] text-white/45">
                  {flowDepthChanged
                    ? 'Depth changes are being tracked for this session.'
                    : 'Unchanged depth is ignored, so the default does not become training data.'}
                </p>
                <div className="mt-2 flex gap-2">
                  <Input
                    ref={flowInputRef}
                    placeholder="Optional flow note"
                    className="border-white/10 bg-black/20 text-white placeholder:text-white/35"
                  />
                  <Button type="button" onClick={markFlowState} className="shrink-0 bg-cyan-300 text-slate-950 hover:bg-cyan-200">
                    <Waves className="mr-2 h-4 w-4" />
                    Mark
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className={cn(
              'space-y-4',
              isSplitFullscreen &&
                'grid flex-1 items-start gap-6 space-y-0 lg:grid-cols-[minmax(280px,420px)_minmax(0,1fr)]'
            )}
          >
            <div className="space-y-4">
              <ProgressRing progress={progress}>
                <span className="font-mono text-4xl font-semibold tabular-nums">{formatStopwatch(elapsedTime)}</span>
                <span className="mt-2 text-xs text-white/55">
                  {Math.max(1, Math.round(elapsedTime / 60))} actual minutes
                </span>
              </ProgressRing>
              <div className="grid grid-cols-3 gap-2 text-center">
                <Metric icon={<Pause className="h-3.5 w-3.5" />} label="Pauses" value={String(pauseCount)} />
                <Metric icon={<Sparkles className="h-3.5 w-3.5" />} label="Flow marks" value={String(flowMarkers.length)} />
                <Metric icon={<Waves className="h-3.5 w-3.5" />} label="Depth changes" value={String(flowDepthChanges.length)} />
              </div>
              <div className="rounded-md border border-cyan-300/20 bg-cyan-300/10 p-3">
                <div className="flex items-center gap-2 text-sm font-medium text-cyan-100">
                  <Gauge className="h-4 w-4" />
                  Actual session telemetry
                </div>
                <p className="mt-1 text-xs text-cyan-50/70">
                  These numbers train the scheduler on what this Activity really cost and returned.
                </p>
              </div>
            </div>

            <div className={cn('space-y-4', isSplitFullscreen && 'rounded-lg border border-white/10 bg-white/[0.04] p-4')}>
            {!isSplitFullscreen && (
              <div className="rounded-md border border-cyan-300/20 bg-cyan-300/10 p-3">
              <div className="flex items-center gap-2 text-sm font-medium text-cyan-100">
                <Gauge className="h-4 w-4" />
                Actual session telemetry
              </div>
              <p className="mt-1 text-xs text-cyan-50/70">
                These numbers train the scheduler on what this Activity really cost and returned.
              </p>
            </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              {scoreFields.map(([key, label]) => (
                <label key={key} className="space-y-1.5">
                  <span className="text-xs text-white/65">{label}</span>
                  <Input
                    type="number"
                    min={0}
                    max={10}
                    value={actualState[key]}
                    onChange={event =>
                      setActualState(prev => ({
                        ...prev,
                        [key]: Math.max(0, Math.min(10, Number(event.target.value))),
                      }))
                    }
                    className="border-white/10 bg-black/20 text-white"
                  />
                </label>
              ))}
            </div>

            <label className="space-y-1.5">
              <span className="text-xs text-white/65">Overall completion after this session</span>
              <Input
                type="number"
                min={0}
                max={100}
                value={completionPercent}
                onChange={event => setCompletionPercent(Math.max(0, Math.min(100, Number(event.target.value))))}
                className="border-white/10 bg-black/20 text-white"
              />
              <Input
                type="range"
                min={0}
                max={100}
                value={completionPercent}
                onChange={event => setCompletionPercent(Math.max(0, Math.min(100, Number(event.target.value))))}
                className="h-8"
              />
              <p className="text-[11px] text-white/45">
                Started at {task.completionPercent ?? 0}%. This session adds{' '}
                {Math.max(0, completionPercent - (task.completionPercent ?? 0))}% if saved.
              </p>
            </label>

            <label className="space-y-1.5">
              <span className="text-xs text-white/65">Emotion word</span>
              {emotionAttribute ? (
                <div className="[&_button]:border-white/10 [&_button]:bg-black/20 [&_button]:text-white">
                  <EnumAttributeInput
                    attr={emotionAttribute}
                    value={emotionWord}
                    onChange={setEmotionWord}
                    onAttributeUpdated={handleAttributeUpdated}
                  />
                </div>
              ) : (
                <Input
                  value={emotionWord}
                  onChange={event => setEmotionWord(event.target.value)}
                  placeholder="Thoughtful, resistant, excited..."
                  className="border-white/10 bg-black/20 text-white placeholder:text-white/35"
                />
              )}
            </label>

            <label className="space-y-1.5">
              <span className="text-xs text-white/65">Notes</span>
              <Textarea
                value={notes}
                onChange={event => setNotes(event.target.value)}
                placeholder="What changed your state? What made starting easier or harder?"
                className="border-white/10 bg-black/20 text-white placeholder:text-white/35"
              />
            </label>

            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                onClick={() => submitFinish(true)}
                disabled={isSubmitting}
                className="min-h-11 bg-cyan-300 text-slate-950 hover:bg-cyan-200"
              >
                Complete activity
              </Button>
              <Button
                type="button"
                onClick={() => submitFinish(false)}
                disabled={isSubmitting}
                variant="outline"
                className="min-h-11 border-white/15 bg-white/5 text-white hover:bg-white/10"
              >
                Save progress
              </Button>
            </div>
            <Button
              type="button"
              variant="ghost"
              className="min-h-11 w-full text-white hover:bg-white/10"
              onClick={cancelSession}
              disabled={isSubmitting}
            >
              <X className="mr-2 h-4 w-4" />
              Cancel session
            </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
    <Dialog open={isExitPromptOpen} onOpenChange={setIsExitPromptOpen}>
      <DialogContent className="border-white/10 bg-slate-950 text-white sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Finish or cancel?</DialogTitle>
          <DialogDescription className="text-white/60">
            Minimize keeps this Activity Session running. Cancel stops it without saving completion data.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:space-x-0">
          <Button
            type="button"
            variant="ghost"
            className="text-white hover:bg-white/10"
            onClick={handleMinimize}
            disabled={isSubmitting}
          >
            <Minimize2 className="mr-2 h-4 w-4" />
            Minimize
          </Button>
          <Button
            type="button"
            variant="outline"
            className="border-white/15 bg-white/5 text-white hover:bg-white/10"
            onClick={cancelSession}
            disabled={isSubmitting}
          >
            <X className="mr-2 h-4 w-4" />
            Cancel session
          </Button>
          <Button
            type="button"
            className="bg-cyan-300 text-slate-950 hover:bg-cyan-200"
            onClick={beginFinish}
            disabled={isSubmitting}
          >
            <CheckCircle2 className="mr-2 h-4 w-4" />
            Finish
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

function Metric({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-md border border-white/10 bg-white/[0.05] p-2">
      <div className="flex items-center justify-center gap-1 text-[10px] uppercase text-white/45">
        {icon}
        {label}
      </div>
      <div className="mt-1 font-mono text-lg text-white">{value}</div>
    </div>
  );
}

function isEmotionAttribute(attr: AttributeDef): boolean {
  return normalizeAttributeName(attr.name) === 'emotion';
}

function normalizeAttributeName(name: unknown): string {
  return String(name ?? '').trim().toLowerCase().replace(/\s+/g, '_');
}

function getEmotionValue(
  customAttributes: Record<string, unknown>,
  emotionAttribute?: AttributeDef
): unknown {
  if (emotionAttribute) {
    const direct = customAttributes[emotionAttribute.attr_id];
    if (direct !== null && direct !== undefined && direct !== '') return direct;
  }
  return customAttributes.emotion_word || customAttributes.emotion || customAttributes.emotion_label || '';
}
